/* eslint-disable indent */
/* globals gtag: true */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import Button from '../buttons/Button';
import Checkbox from '../buttons/Checkbox';
import Popup from './Popup';

import styles from '../../../styles/partials/cookies.module.css';

interface ConsentItem {
    id: string;
    label: string;
    description: string;
    value: boolean;
    disabled?: boolean;
}

interface CookiesProps {
    consent?: ConsentItem[];
    onClose?: ((values: ConsentItem[]) => void) | null;
}

function CookiesComponent({
    consent = [
        {
            id: 'functionality_storage',
            label: 'Fonctionnel',
            description: 'Témoins requis pour le bon fonctionnement du site',
            value: true,
            disabled: true,
        },
        {
            id: 'analytics_storage',
            label: 'Statistiques',
            description:
                'Témoins utilisés pour mesurer le comportement des utilisateurs sur le site, leurs données de navigation et leur localisation (Google Analytics)',
            value: false,
        },
        {
            id: 'ad_storage',
            label: 'Publicité',
            description: 'Témoins utilisés pour le ciblage publicitaire (Google Ads, Facebook)',
            value: false,
        },
        {
            id: 'personalization_storage',
            label: 'Publicité culturelle',
            description:
                'Témoins utilisés pour le ciblage publicitaire de nos partenaires culturels locaux (Data Coop)',
            value: false,
        },
        {
            id: 'show_widgets',
            label: 'Intégrations tierces',
            description:
                'Intégrations externes (iframe) et lecteurs médias utilisés par notre site (Google Maps, Spotify).',
            value: false,
        },
    ],
    onClose = null,
}: CookiesProps) {
    const route = useUrlGenerator();
    const [choices, setChoices] = useState<ConsentItem[]>(
        (consent || []).map((it) => ({
            ...it,
            value:
                Cookies.get(it.id) === 'granted'
                    ? true
                    : Cookies.get(it.id) === 'denied'
                      ? false
                      : it.value,
        })),
    );

    const onSubmit = useCallback(
        (values: ConsentItem[]) => {
            const gtagItems = (values || []).reduce(
                (acc, { id, value = false }) => {
                    const nextValue = value ? 'granted' : 'denied';
                    Cookies.set(id, nextValue, { secure: true, expires: 182 });

                    if (id.match(/_storage$/)) {
                        return { ...acc, [id]: nextValue };
                    }

                    return acc;
                },
                {} as Record<string, string>,
            );

            if (typeof gtag !== 'undefined') {
                gtag('consent', 'update', gtagItems);
                gtag('event', 'Consent');
            }

            Cookies.set('show_cookie_consent', 'false', { secure: true, expires: 182 });

            if (onClose !== null) {
                onClose(values || []);
            }
        },
        [onClose],
    );

    const onClickChoice = useCallback(
        (id: string, newValue: boolean) => {
            setChoices(
                (choices || []).reduce((acc, it) => {
                    if (it.id === id) {
                        return [...acc, { ...it, value: newValue }];
                    }
                    return [...acc, it];
                }, [] as ConsentItem[]),
            );
        },
        [choices],
    );

    const onClickConfirm = useCallback(() => {
        onSubmit(choices);
    }, [choices, onSubmit]);

    const onClickAccept = useCallback(() => {
        const items = consent.map((it) => ({ ...it, value: !it.disabled ? true : it.value }));
        setChoices(items);
        onSubmit(items);
    }, [consent, onSubmit]);

    const onClickDeny = useCallback(() => {
        const items = consent.map((it) => ({ ...it, value: !it.disabled ? false : it.value }));
        setChoices(items);
        onSubmit(items);
    }, [consent, onSubmit]);

    const onClickTerms = useCallback(() => {
        if (onClose !== null) {
            onClose([]);
        }
    }, [onClose]);

    return (
        <Popup
            className={styles.container}
            isSmall
            isGray
            title="Vos paramètres de confidentialité"
        >
            <div className={styles.more}>
                <p className={styles.description}>
                    <FormattedMessage
                        defaultMessage="Nous plaçons des témoins de connexion sur votre appareil. Si vous souhaitez accepter notre utilisation des témoins, cliquez sur le bouton « Tout accepter ». Vous pouvez aussi choisir les catégories de témoins de connexion que vous acceptez en cochant les cases ou même choisir de n'accepter que les témoins nécessaires en cliquant sur le bouton «Tout rejeter». Pour obtenir des informations complémentaires, veuillez consulter notre "
                        description="Cookies consent intro text"
                    />{' '}
                    <Link
                        href={route('pages.show', {
                            page: 'politique-de-confidentialite',
                        })}
                        onClick={onClickTerms}
                    >
                        <FormattedMessage
                            defaultMessage="politique de confidentialité"
                            description="Privacy policy link text"
                        />
                    </Link>
                    .
                </p>
            </div>
            {(choices || []).map(
                ({ id, label = null, value = false, description, disabled = false }) => (
                    <div className={styles.choice} key={`cookie-${id}`}>
                        <div className={styles.checkbox}>
                            <Checkbox
                                checked={value === true}
                                disabled={disabled}
                                isBlack
                                onClick={() => onClickChoice(id, !value)}
                                label={label}
                            >
                                <span>{label}</span>
                            </Checkbox>
                        </div>
                        <p className={styles.description}>{description}</p>
                    </div>
                ),
            )}
            <div className={styles.buttons}>
                <Button className={styles.button} small onClick={onClickConfirm}>
                    <FormattedMessage
                        defaultMessage="Enregistrer mes paramètres"
                        description="Save cookie settings button"
                    />
                </Button>
            </div>
            <div className={classNames([styles.buttons, styles.second])}>
                <Button className={styles.button} small onClick={onClickAccept}>
                    <FormattedMessage
                        defaultMessage="Tout accepter"
                        description="Accept all cookies button"
                    />
                </Button>
                <Button className={styles.button} small onClick={onClickDeny}>
                    <FormattedMessage
                        defaultMessage="Tout rejeter"
                        description="Reject all cookies button"
                    />
                </Button>
            </div>
        </Popup>
    );
}

export default CookiesComponent;
